import { Button, toast } from "@devhop/ui";
import { PaperclipIcon, TrashIcon, UploadIcon } from "lucide-react";
import { useRef, useState } from "react";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import type { ExpenseAttachmentDTO } from "../api/client";
import {
  useAddExpenseAttachmentMutation,
  useDeleteExpenseAttachmentMutation,
} from "../api/queries";

type ExpenseAttachmentUploaderProps = {
  expenseId: string;
  attachments: ExpenseAttachmentDTO[];
  canUpload: boolean;
  canDelete: boolean;
};

export function ExpenseAttachmentUploader({
  expenseId,
  attachments,
  canUpload,
  canDelete,
}: ExpenseAttachmentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const addAttachment = useAddExpenseAttachmentMutation(expenseId);
  const deleteAttachment = useDeleteExpenseAttachmentMutation(expenseId);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setUploading(true);
    try {
      const key = `expenses/${expenseId}/${crypto.randomUUID()}-${file.name}`;
      const { uploadUrl, key: storageKey } = await uploadApi.getPresignUrl({
        key,
        contentType: file.type || undefined,
      });
      await uploadFileToPresignedUrl(uploadUrl, file, {
        contentType: file.type || undefined,
      });
      await addAttachment.mutateAsync({
        storageKey,
        fileName: file.name,
        contentType: file.type || undefined,
      });
    } catch {
      toast.error("ອັບໂຫຼດໄຟລ໌ລົ້ມເຫຼວ");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {canUpload && (
        <div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading || addAttachment.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <UploadIcon className="mr-2 size-4" />
            {uploading ? "ກຳລັງອັບໂຫຼດ..." : "ແນບໄຟລ໌"}
          </Button>
        </div>
      )}

      {attachments.length === 0 ? (
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີໄຟລ໌ແນບ</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((file) => (
            <li
              key={file.id}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <PaperclipIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{file.fileName}</span>
              </div>
              {canDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  disabled={deleteAttachment.isPending}
                  onClick={() => deleteAttachment.mutate(file.id)}
                >
                  <TrashIcon className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
